import { Modal } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdCheckCircle,
  MdErrorOutline,
  MdTableChart,
  MdUpload,
} from "react-icons/md";
import Button from "shared/components/Button";
import { NAME_SPACES } from "shared/locales/constants";

function parseCSVLine(line, delimiter) {
  const result = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === delimiter && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }

  result.push(current.trim());
  return result;
}

export default function CSVMappingModal({
  isOpen,
  onClose,
  onImport,
  csvFile,
}) {
  const [headers, setHeaders] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [mapping, setMapping] = useState({
    email: null,
    firstName: null,
    lastName: null,
  });
  const [importing, setImporting] = useState(false);
  const [validEmails, setValidEmails] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.COMPANY);

  const validateMapping = useCallback((currentMapping, lines, delimiter) => {
    if (currentMapping.email === null) {
      setValidEmails(0);
      return;
    }

    let count = 0;
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i], delimiter);
      const email = values[currentMapping.email]?.trim();
      if (email && email.includes("@")) {
        count++;
      }
    }
    setValidEmails(count);
  }, []);

  const parseCSV = useCallback(async () => {
    if (!csvFile) return;

    const text = await csvFile.text();
    const lines = text.split("\n").filter((line) => line.trim());

    if (lines.length === 0) return;

    const delimiter = lines[0].includes(";") ? ";" : ",";
    const parsedHeaders = parseCSVLine(lines[0], delimiter);
    setHeaders(parsedHeaders);

    const preview = lines
      .slice(1, Math.min(6, lines.length))
      .map((line) => parseCSVLine(line, delimiter));
    setPreviewData(preview);
    setTotalRows(lines.length - 1);

    const autoMapping = {
      email: null,
      firstName: null,
      lastName: null,
    };

    parsedHeaders.forEach((header, index) => {
      const lower = header.toLowerCase();
      if (
        lower.includes("email") ||
        lower.includes("mail") ||
        lower.includes("e-mail")
      ) {
        autoMapping.email = index;
      } else if (
        lower.includes("prénom") ||
        lower.includes("prenom") ||
        lower.includes("firstname") ||
        lower.includes("first name")
      ) {
        autoMapping.firstName = index;
      } else if (
        lower.includes("nom") &&
        !lower.includes("prénom") &&
        !lower.includes("prenom")
      ) {
        autoMapping.lastName = index;
      }
    });

    setMapping(autoMapping);
    validateMapping(autoMapping, lines, delimiter);
  }, [csvFile, validateMapping]);

  useEffect(() => {
    if (isOpen && csvFile) {
      parseCSV();
    }
  }, [isOpen, csvFile, parseCSV]);

  function handleMappingChange(field, value) {
    const newMapping = { ...mapping, [field]: value };
    setMapping(newMapping);

    if (csvFile) {
      csvFile.text().then((text) => {
        const lines = text.split("\n").filter((line) => line.trim());
        const delimiter = lines[0].includes(";") ? ";" : ",";
        validateMapping(newMapping, lines, delimiter);
      });
    }
  }

  async function handleImport() {
    if (!csvFile || mapping.email === null) return;

    setImporting(true);

    const text = await csvFile.text();
    const lines = text.split("\n").filter((line) => line.trim());
    const delimiter = lines[0].includes(";") ? ";" : ",";

    const mappedData = [];

    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i], delimiter);
      const email = values[mapping.email]?.trim();

      if (email && email.includes("@")) {
        mappedData.push({
          email,
          firstName:
            mapping.firstName !== null
              ? values[mapping.firstName]?.trim() || ""
              : "",
          lastName:
            mapping.lastName !== null
              ? values[mapping.lastName]?.trim() || ""
              : "",
        });
      }
    }

    setImporting(false);
    onImport(mappedData);
    onClose();
  }

  const canImport = mapping.email !== null && validEmails > 0;

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      width="80rem"
      centered
      footer={null}
      className="company__csv-mapping-modal"
      title={
        <div className="company__flex company__items-center company__gap-3">
          <div className="company__w-10 company__h-10 company__bg-green-600 company__rounded-lg company__flex company__items-center company__justify-center">
            <MdTableChart className="company__w-5 company__h-5 company__text-white" />
          </div>
          <div>
            <h2 className="company__text-xl company__font-semibold company__text-slate-900 company__m-0">
              {t("DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.HEADER.TITLE")}
            </h2>
            <p className="company__text-sm company__text-slate-600 company__m-0">
              {t("DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.HEADER.ROWS", {
                count: totalRows,
              })}
            </p>
          </div>
        </div>
      }
    >
      <div
        className="company__space-y-6"
        style={{ maxHeight: "calc(90vh - 200px)", overflowY: "auto" }}
      >
        <div className="company__info-box company__p-4 company__flex company__gap-3">
          <MdErrorOutline
            className="company__w-5 company__h-5 company__text-blue-600 company__flex-shrink-0"
            style={{ marginTop: "0.125rem" }}
          />
          <div className="company__text-sm company__text-blue-900">
            <p className="company__font-medium company__mb-1">
              {t("DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.INFO_BOX.TITLE")}
            </p>
            <p>
              {t("DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.INFO_BOX.DESCRIPTION")}
            </p>
          </div>
        </div>

        <div className="company__space-y-4">
          <h3 className="company__text-lg company__font-semibold company__text-slate-900">
            {t("DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.FIELDS.TITLE")}
          </h3>

          <div className="company__grid company__grid-cols-1 company__md-grid-cols-3 company__gap-4">
            <div className="company__bg-slate-50 company__rounded-lg company__border-2 company__border-slate-200 company__p-4">
              <label className="company__block company__text-sm company__font-medium company__text-slate-900 company__mb-2">
                {t("DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.FIELDS.EMAIL.LABEL")}{" "}
                <span className="company__text-red-600">*</span>
              </label>
              <select
                value={mapping.email !== null ? mapping.email : ""}
                onChange={(e) =>
                  handleMappingChange(
                    "email",
                    e.target.value === "" ? null : parseInt(e.target.value),
                  )
                }
                className="company__w-full company__px-3 company__py-2 company__border company__border-slate-300 company__rounded-lg company__focus-ring-2 company__focus-ring-blue-500 company__focus-border-transparent"
              >
                <option value="">
                  {t("DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.FIELDS.PLACEHOLDER")}
                </option>
                {headers.map((header, index) => (
                  <option key={index} value={index}>
                    {header ||
                      t(
                        "DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.FIELDS.DEFAULT_HEADER",
                        { index: index + 1 },
                      )}
                  </option>
                ))}
              </select>
              {mapping.email !== null && (
                <p className="company__text-xs company__text-green-600 company__mt-1 company__flex company__items-center company__gap-2">
                  <MdCheckCircle className="company__w-3 company__h-3" />
                  {t(
                    "DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.FIELDS.VALID_EMAILS",
                    { count: validEmails },
                  )}
                </p>
              )}
            </div>

            <div className="company__bg-slate-50 company__rounded-lg company__border-2 company__border-slate-200 company__p-4">
              <label className="company__block company__text-sm company__font-medium company__text-slate-900 company__mb-2">
                {t(
                  "DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.FIELDS.FIRSTNAME.LABEL",
                )}{" "}
                <span className="company__text-slate-400">
                  {t("DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.FIELDS.OPTIONAL")}
                </span>
              </label>
              <select
                value={mapping.firstName !== null ? mapping.firstName : ""}
                onChange={(e) =>
                  handleMappingChange(
                    "firstName",
                    e.target.value === "" ? null : parseInt(e.target.value),
                  )
                }
                className="company__w-full company__px-3 company__py-2 company__border company__border-slate-300 company__rounded-lg company__focus-ring-2 company__focus-ring-blue-500 company__focus-border-transparent"
              >
                <option value="">
                  {t("DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.FIELDS.PLACEHOLDER")}
                </option>
                {headers.map((header, index) => (
                  <option key={index} value={index}>
                    {header ||
                      t(
                        "DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.FIELDS.DEFAULT_HEADER",
                        { index: index + 1 },
                      )}
                  </option>
                ))}
              </select>
            </div>

            <div className="company__bg-slate-50 company__rounded-lg company__border-2 company__border-slate-200 company__p-4">
              <label className="company__block company__text-sm company__font-medium company__text-slate-900 company__mb-2">
                {t(
                  "DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.FIELDS.LASTNAME.LABEL",
                )}{" "}
                <span className="company__text-slate-400">
                  {t("DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.FIELDS.OPTIONAL")}
                </span>
              </label>
              <select
                value={mapping.lastName !== null ? mapping.lastName : ""}
                onChange={(e) =>
                  handleMappingChange(
                    "lastName",
                    e.target.value === "" ? null : parseInt(e.target.value),
                  )
                }
                className="company__w-full company__px-3 company__py-2 company__border company__border-slate-300 company__rounded-lg company__focus-ring-2 company__focus-ring-blue-500 company__focus-border-transparent"
              >
                <option value="">
                  {t("DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.FIELDS.PLACEHOLDER")}
                </option>
                {headers.map((header, index) => (
                  <option key={index} value={index}>
                    {header ||
                      t(
                        "DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.FIELDS.DEFAULT_HEADER",
                        { index: index + 1 },
                      )}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="company__space-y-4">
          <div className="company__flex company__items-center company__justify-between">
            <h3 className="company__text-lg company__font-semibold company__text-slate-900">
              {t("DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.PREVIEW.TITLE")}
            </h3>
            <span className="company__text-sm company__text-slate-600">
              {t("DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.PREVIEW.SUBTITLE")}
            </span>
          </div>

          <div className="company__border company__border-slate-200 company__rounded-lg company__overflow-hidden">
            <div className="company__overflow-x-auto">
              <table className="company__w-full">
                <thead className="company__bg-slate-100">
                  <tr>
                    <th className="company__px-3 company__py-2 company__text-left company__text-xs company__font-medium company__text-slate-600 company__uppercase">
                      #
                    </th>
                    {headers.map((header, index) => {
                      let badge = null;
                      if (mapping.email === index) {
                        badge = (
                          <span className="company__ml-2 company__px-2 company__py-1 company__bg-blue-600 company__text-white company__text-xs company__rounded">
                            {t(
                              "DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.FIELDS.EMAIL.LABEL",
                            )}
                          </span>
                        );
                      } else if (mapping.firstName === index) {
                        badge = (
                          <span className="company__ml-2 company__px-2 company__py-1 company__bg-green-600 company__text-white company__text-xs company__rounded">
                            {t(
                              "DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.FIELDS.FIRSTNAME.LABEL",
                            )}
                          </span>
                        );
                      } else if (mapping.lastName === index) {
                        badge = (
                          <span className="company__ml-2 company__px-2 company__py-1 company__bg-purple-600 company__text-white company__text-xs company__rounded">
                            {t(
                              "DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.FIELDS.LASTNAME.LABEL",
                            )}
                          </span>
                        );
                      }

                      return (
                        <th
                          key={index}
                          className={`company__px-3 company__py-2 company__text-left company__text-xs company__font-medium company__uppercase ${
                            mapping.email === index ||
                            mapping.firstName === index ||
                            mapping.lastName === index
                              ? "company__bg-slate-200 company__text-slate-900"
                              : "company__text-slate-600"
                          }`}
                        >
                          <div className="company__flex company__items-center">
                            {header ||
                              t(
                                "DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.FIELDS.DEFAULT_HEADER_SHORT",
                                { index: index + 1 },
                              )}
                            {badge}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="company__divide-y company__divide-slate-200 company__bg-white">
                  {previewData.map((row, rowIndex) => (
                    <tr key={rowIndex} className="company__hover-bg-slate-50">
                      <td className="company__px-3 company__py-2 company__text-xs company__text-slate-500">
                        {rowIndex + 1}
                      </td>
                      {row.map((cell, cellIndex) => (
                        <td
                          key={cellIndex}
                          className={`company__px-3 company__py-2 company__text-sm ${
                            mapping.email === cellIndex ||
                            mapping.firstName === cellIndex ||
                            mapping.lastName === cellIndex
                              ? "company__bg-slate-50 company__font-medium company__text-slate-900"
                              : "company__text-slate-700"
                          }`}
                        >
                          {cell || "-"}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {previewData.length < totalRows && (
            <p className="company__text-xs company__text-slate-500 company__text-center">
              {t("DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.PREVIEW.MORE_ROWS", {
                count: totalRows - previewData.length,
              })}
            </p>
          )}
        </div>

        {!canImport && (
          <div className="company__bg-amber-50 company__border company__border-amber-200 company__rounded-lg company__p-4">
            <div className="company__flex company__gap-3">
              <MdErrorOutline
                className="company__w-5 company__h-5 company__text-amber-600 company__flex-shrink-0"
                style={{ marginTop: "0.125rem" }}
              />
              <div className="company__text-sm company__text-amber-900">
                <p className="company__font-medium">
                  {t(
                    "DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.STATES.MISSING_EMAIL.TITLE",
                  )}
                </p>
                <p>
                  {t(
                    "DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.STATES.MISSING_EMAIL.DESCRIPTION",
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {canImport && (
          <div className="company__bg-green-50 company__border company__border-green-200 company__rounded-lg company__p-4">
            <div className="company__flex company__gap-3">
              <MdCheckCircle
                className="company__w-5 company__h-5 company__text-green-600 company__flex-shrink-0"
                style={{ marginTop: "0.125rem" }}
              />
              <div className="company__text-sm company__text-green-900">
                <p className="company__font-medium">
                  {t("DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.STATES.READY.TITLE")}
                </p>
                <p>
                  {t(
                    "DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.STATES.READY.DESCRIPTION",
                    { count: validEmails },
                  )}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="company__px-6 company__py-4 company__border-t company__border-slate-200 company__bg-slate-50 company__flex company__items-center company__justify-between company__mt-6">
        <Button onClick={onClose} type="secondary">
          {t("DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.ACTIONS.CANCEL")}
        </Button>
        <Button
          onClick={handleImport}
          disabled={!canImport || importing}
          type="success"
        >
          <MdUpload className="company__w-4 company__h-4" />
          {importing
            ? t("DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.ACTIONS.IMPORT_LOADING")
            : t("DEPLOYMENT_MANAGER.CSV_MAPPING_MODAL.ACTIONS.IMPORT", {
                count: validEmails,
              })}
        </Button>
      </div>
    </Modal>
  );
}
